/**
 * An athlete's details — position, jersey, height, weight, date of birth —
 * checked across the record, ESPN and Sleeper.
 *
 * profileSync keeps the record in step with ESPN for four of these. This is
 * the second opinion: whether Sleeper backs ESPN up, and whether the record
 * disagrees with sources that agree with each other — the case that put a
 * wrong birthday on Sam LaPorta's calendar (record 27 Nov 2000; ESPN and
 * Sleeper both 12 Jan 2001). It changes nothing; it says where they differ.
 *
 * TOLERANCES, from ESPN and Sleeper laid side by side for 595 players on
 * 30 Sep 2026:
 *   - date of birth: 7 disagreed, each a real conflict. Exact.
 *   - jersey: 14 disagreed, one side stale. Exact.
 *   - height: 7 disagreed, every one by a single inch — two tape measures, not
 *     two facts. Within an inch agrees.
 *   - weight: 54 disagreed, by 2 to 15 lb; weights drift through a season.
 *     Within 10 lb agrees.
 *   - position: 169 "disagreed", nearly all vocabulary — Sleeper writes DB for
 *     S and CB, T or OL for OT, K for PK. Compared by position group; only a
 *     different group is a disagreement.
 *
 * Pure; the profile sync job runs it and the athlete page reads the result.
 */

import { normalizeHeight, normalizeJersey, normalizePosition, normalizeWeight } from './profileSync.js'

export const DETAIL_FIELDS = ['position', 'jersey_number', 'height', 'weight', 'date_of_birth'] as const
export type DetailField = (typeof DETAIL_FIELDS)[number]

export const DETAIL_LABELS: Record<DetailField, string> = {
  position: 'position', jersey_number: 'jersey number', height: 'height', weight: 'weight', date_of_birth: 'date of birth',
}

export type DetailVerdict = 'agrees' | 'record_differs' | 'sources_differ' | 'one_source' | 'unknown'

export interface DetailCheck {
  field: DetailField
  verdict: DetailVerdict
  /** Each side as a person reads it: "6' 2\"", "225 lbs", "2001-01-12". Null when that side has nothing. */
  record: string | null
  espn: string | null
  sleeper: string | null
}

export interface ProfileCrossCheck {
  verdict: 'agrees' | 'differs' | 'partial'
  fields: DetailCheck[]
  /** One sentence per field that disagrees. */
  points: string[]
  /** The same sentences with the field and kind each is about, for whoever decides who to tell. */
  findings: { field: DetailField; verdict: DetailVerdict; text: string }[]
}

export type DetailValues = Partial<Record<DetailField, unknown>>

function positionGroup(v: unknown): string | null {
  const p = normalizePosition(v)
  if (!p) return null
  if (['DE', 'DT', 'DL', 'NT', 'EDGE', 'OLB', 'LB', 'ILB', 'MLB'].includes(p)) return 'front seven'
  if (['CB', 'S', 'FS', 'SS', 'DB', 'SAF'].includes(p)) return 'secondary'
  if (['OT', 'OG', 'C', 'OL', 'T', 'G', 'IOL'].includes(p)) return 'offensive line'
  if (['RB', 'FB', 'HB'].includes(p)) return 'running back'
  if (['K', 'PK'].includes(p)) return 'kicker'
  return p
}

function dateOf(v: unknown): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? '').trim())
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}

/** How a field compares: a normal form for equality, and a tolerance where two honest measurements differ. */
const RULES: Record<DetailField, { key: (v: unknown) => string | null; same: (a: string, b: string) => boolean }> = {
  position: { key: positionGroup, same: (a, b) => a === b },
  jersey_number: { key: normalizeJersey, same: (a, b) => a === b },
  height: { key: normalizeHeight, same: (a, b) => Math.abs(Number(a) - Number(b)) <= 1 },
  weight: { key: normalizeWeight, same: (a, b) => Math.abs(Number(a) - Number(b)) < 10 },
  date_of_birth: { key: dateOf, same: (a, b) => a === b },
}

const shown = (v: unknown): string | null => {
  if (v == null) return null
  const s = String(v).trim()
  return s === '' ? null : s
}

export function checkDetail(field: DetailField, record: unknown, espn: unknown, sleeper: unknown): DetailCheck {
  const rule = RULES[field]
  const [r, e, s] = [rule.key(record), rule.key(espn), rule.key(sleeper)]
  const out = (verdict: DetailVerdict): DetailCheck => ({
    field, verdict,
    record: r == null ? null : field === 'date_of_birth' ? r : shown(record),
    espn: e == null ? null : field === 'date_of_birth' ? e : shown(espn),
    sleeper: s == null ? null : field === 'date_of_birth' ? s : shown(sleeper),
  })
  if (e != null && s != null && !rule.same(e, s)) return out('sources_differ')
  const outside = e ?? s
  if (outside == null) return out('unknown')
  if (r != null && !rule.same(r, outside)) {
    // Two sources agreeing against the record is the strongest signal there is.
    return out(e != null && s != null ? 'record_differs' : 'one_source')
  }
  return out(e != null && s != null ? 'agrees' : 'one_source')
}

function sentence(c: DetailCheck, name: string): string | null {
  const label = DETAIL_LABELS[c.field]
  if (c.verdict === 'sources_differ') return `ESPN has ${name}'s ${label} as ${c.espn}; Sleeper has ${c.sleeper}.`
  if (c.verdict === 'record_differs') return `ESPN and Sleeper both have ${name}'s ${label} as ${c.espn}; the record says ${c.record}.`
  if (c.verdict === 'one_source' && c.record != null && (c.espn ?? c.sleeper) != null && !RULES[c.field].same(RULES[c.field].key(c.record)!, RULES[c.field].key(c.espn ?? c.sleeper)!)) {
    return `${c.espn != null ? 'ESPN' : 'Sleeper'} has ${name}'s ${label} as ${c.espn ?? c.sleeper}; the record says ${c.record}. No second source to settle it.`
  }
  return null
}

export function crossCheckProfile(input: { name: string; record: DetailValues; espn: DetailValues; sleeper: DetailValues | null }): ProfileCrossCheck {
  const fields = DETAIL_FIELDS.map(f => checkDetail(f, input.record[f], input.espn[f], input.sleeper?.[f]))
  const findings = fields.flatMap(c => {
    const text = sentence(c, input.name)
    return text ? [{ field: c.field, verdict: c.verdict, text }] : []
  })
  const points = findings.map(f => f.text)
  const verdict: ProfileCrossCheck['verdict'] = points.length ? 'differs' : fields.every(c => c.verdict === 'agrees' || c.verdict === 'unknown') ? 'agrees' : 'partial'
  return { verdict, fields, points, findings }
}
